/**
 * Cortex Affect dashboard (Cortex-06 v17-02)
 *
 * 4 cards de métricas R5..R7 + heatmap VAD × outcome + drill-down pipeline_runs.
 * Lê de:
 *   - pipeline_runs (turnos, response_style_tag, status)
 *   - affect_states (valence, arousal, dominance)
 *   - threat_detections, tom_models (para staleness/update rate)
 *   - sentinel_alerts (cortex.* das últimas 24h)
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useOrgRole } from "@/hooks/useOrgRole";
import { Activity, AlertTriangle, Heart, Users, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Metrics {
  pipeline_error_rate: number | null;
  pipeline_total_1h: number;
  threat_low_pct: number | null;
  tom_update_rate: number | null;
  affect_staleness_min: number | null;
  affect_count_1h: number;
}

interface PipelineRun {
  id: string;
  turn_id: string;
  group_id: string;
  group_state: string | null;
  affect_valence: number | null;
  affect_arousal: number | null;
  response_style_tag: string | null;
  status: string;
  model_used: string | null;
  created_at: string;
}

interface AffectPoint {
  valence: number;
  arousal: number;
  created_at: string;
}

const STYLE_BADGE: Record<string, string> = {
  de_escalate: "bg-destructive/15 text-destructive border-destructive/30",
  empathic: "bg-primary/15 text-primary border-primary/30",
  cautious: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  energetic: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  warm: "bg-rose-500/15 text-rose-600 border-rose-500/30",
  default_neutral: "bg-muted text-muted-foreground border-border",
};

export default function CortexAffect() {
  const { orgId } = useOrgRole();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [affectPoints, setAffectPoints] = useState<AffectPoint[]>([]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const sinceH = new Date(Date.now() - 3600_000).toISOString();
      const since24 = new Date(Date.now() - 86_400_000).toISOString();

      const [
        pipeTotalQ,
        pipeErrQ,
        threatTotalQ,
        threatLowQ,
        tomActiveQ,
        msgGroupsQ,
        affectLatestQ,
        affectCountQ,
        runsQ,
        affectQ,
      ] = await Promise.all([
        supabase.from("pipeline_runs").select("*", { count: "exact", head: true }).eq("org_id", orgId).gte("created_at", sinceH),
        supabase.from("pipeline_runs").select("*", { count: "exact", head: true }).eq("org_id", orgId).neq("status", "success").gte("created_at", sinceH),
        supabase.from("threat_detections").select("*", { count: "exact", head: true }).eq("org_id", orgId).gte("created_at", since24),
        supabase.from("threat_detections").select("*", { count: "exact", head: true }).eq("org_id", orgId).eq("severity", "low").gte("created_at", since24),
        supabase.from("tom_models").select("group_id").eq("org_id", orgId).gte("updated_at", sinceH).limit(2000),
        supabase.from("syra_messages").select("group_id").eq("org_id", orgId).gte("created_at", sinceH).limit(2000),
        supabase.from("affect_states").select("created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(1),
        supabase.from("affect_states").select("*", { count: "exact", head: true }).eq("org_id", orgId).gte("created_at", sinceH),
        supabase.from("pipeline_runs").select("id, turn_id, group_id, group_state, affect_valence, affect_arousal, response_style_tag, status, model_used, created_at")
          .eq("org_id", orgId).order("created_at", { ascending: false }).limit(50),
        supabase.from("affect_states").select("valence, arousal, created_at").eq("org_id", orgId)
          .gte("created_at", since24).order("created_at", { ascending: false }).limit(500),
      ]);

      if (cancelled) return;

      const pipeTotal = pipeTotalQ.count ?? 0;
      const pipeErr = pipeErrQ.count ?? 0;
      const threatTotal = threatTotalQ.count ?? 0;
      const threatLow = threatLowQ.count ?? 0;
      const expected = new Set((msgGroupsQ.data ?? []).map((m: any) => m.group_id)).size;
      const observed = new Set((tomActiveQ.data ?? []).map((m: any) => m.group_id)).size;
      const latestAffect = affectLatestQ.data?.[0]?.created_at;

      setMetrics({
        pipeline_error_rate: pipeTotal > 0 ? (pipeErr / pipeTotal) * 100 : null,
        pipeline_total_1h: pipeTotal,
        threat_low_pct: threatTotal > 0 ? (threatLow / threatTotal) * 100 : null,
        tom_update_rate: expected > 0 ? (observed / expected) * 100 : null,
        affect_staleness_min: latestAffect ? (Date.now() - new Date(latestAffect).getTime()) / 60_000 : null,
        affect_count_1h: affectCountQ.count ?? 0,
      });
      setRuns((runsQ.data ?? []) as PipelineRun[]);
      setAffectPoints((affectQ.data ?? []) as AffectPoint[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // Heatmap VAD: bucketiza valence (5) × arousal (5) → contagem
  const heatmap = useMemo(() => {
    const grid: number[][] = Array.from({ length: 5 }, () => Array(5).fill(0));
    affectPoints.forEach((p) => {
      const v = Math.max(0, Math.min(4, Math.floor(((p.valence + 1) / 2) * 5)));
      const a = Math.max(0, Math.min(4, Math.floor(p.arousal * 5)));
      grid[a][v] += 1;
    });
    const max = Math.max(1, ...grid.flat());
    return { grid, max };
  }, [affectPoints]);

  if (loading || !metrics) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const m = metrics;
  const cards = [
    {
      title: "Pipeline error rate (1h)",
      icon: Activity,
      value: m.pipeline_error_rate === null ? "—" : `${m.pipeline_error_rate.toFixed(1)}%`,
      sub: `${m.pipeline_total_1h} turnos`,
      target: "≤ 2%",
      ok: m.pipeline_error_rate === null ? null : m.pipeline_error_rate <= 2,
    },
    {
      title: "Threat low share (24h)",
      icon: AlertTriangle,
      value: m.threat_low_pct === null ? "—" : `${m.threat_low_pct.toFixed(1)}%`,
      sub: "proxy de false-positive",
      target: "≤ 8%",
      ok: m.threat_low_pct === null ? null : m.threat_low_pct <= 8,
    },
    {
      title: "ToM update rate (1h)",
      icon: Users,
      value: m.tom_update_rate === null ? "—" : `${m.tom_update_rate.toFixed(0)}%`,
      sub: "grupos ativos cobertos",
      target: "≥ 95%",
      ok: m.tom_update_rate === null ? null : m.tom_update_rate >= 95,
    },
    {
      title: "Affect staleness",
      icon: Heart,
      value: m.affect_staleness_min === null ? "ausente" : `${m.affect_staleness_min.toFixed(0)} min`,
      sub: `${m.affect_count_1h} snapshots/1h`,
      target: "≤ 10 min",
      ok: m.affect_staleness_min === null ? false : m.affect_staleness_min <= 10,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Cards de métricas (Cortex-05) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.title}>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <c.icon className="w-4 h-4" />
                {c.title}
              </CardTitle>
              {c.ok !== null && (
                <Badge variant={c.ok ? "outline" : "destructive"} className="text-xs">
                  {c.ok ? "ok" : "fora"}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{c.value}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {c.sub} · alvo {c.target}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="heatmap">
        <TabsList>
          <TabsTrigger value="heatmap">Heatmap VAD (24h)</TabsTrigger>
          <TabsTrigger value="runs">Pipeline runs (últimos 50)</TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Distribuição valência × arousal</CardTitle>
            </CardHeader>
            <CardContent>
              {affectPoints.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Sem snapshots de affect nas últimas 24h.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20 text-right">arousal ↑</span>
                    <div className="flex-1 grid grid-cols-5 gap-1">
                      {[...heatmap.grid].reverse().map((row, ri) => (
                        row.map((count, ci) => {
                          const intensity = count / heatmap.max;
                          return (
                            <div
                              key={`${ri}-${ci}`}
                              className="aspect-square rounded border border-border flex items-center justify-center text-xs font-medium"
                              style={{
                                backgroundColor: `hsl(var(--primary) / ${0.05 + intensity * 0.85})`,
                                color: intensity > 0.5 ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                              }}
                              title={`v=${(ci * 0.4 - 1).toFixed(1)}..${(ci * 0.4 - 0.6).toFixed(1)} a=${((4 - ri) * 0.2).toFixed(1)}..${((5 - ri) * 0.2).toFixed(1)} → ${count}`}
                            >
                              {count > 0 ? count : ""}
                            </div>
                          );
                        })
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20 text-right" />
                    <div className="flex-1 grid grid-cols-5 gap-1 text-[10px] text-muted-foreground text-center">
                      <span>negativo</span><span>↓</span><span>neutro</span><span>↑</span><span>positivo</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Total {affectPoints.length} snapshots · pico {heatmap.max} no bin mais quente
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Turnos recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {runs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Nenhum pipeline_run registrado ainda. Aguarde turnos chegarem.
                </p>
              ) : (
                <ScrollArea className="h-[480px]">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground text-left">
                      <tr className="border-b border-border">
                        <th className="py-2 pr-2">quando</th>
                        <th className="py-2 pr-2">estado</th>
                        <th className="py-2 pr-2">v / a</th>
                        <th className="py-2 pr-2">style</th>
                        <th className="py-2 pr-2">model</th>
                        <th className="py-2 pr-2">status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r) => (
                        <tr key={r.id} className="border-b border-border/50 hover:bg-muted/40">
                          <td className="py-2 pr-2 text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(r.created_at), { locale: ptBR, addSuffix: true })}
                          </td>
                          <td className="py-2 pr-2 text-xs">{r.group_state ?? "—"}</td>
                          <td className="py-2 pr-2 text-xs font-mono">
                            {r.affect_valence?.toFixed(2) ?? "—"} / {r.affect_arousal?.toFixed(2) ?? "—"}
                          </td>
                          <td className="py-2 pr-2">
                            {r.response_style_tag ? (
                              <Badge variant="outline" className={`text-xs ${STYLE_BADGE[r.response_style_tag] ?? ""}`}>
                                {r.response_style_tag}
                              </Badge>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </td>
                          <td className="py-2 pr-2 text-xs text-muted-foreground">{r.model_used ?? "—"}</td>
                          <td className="py-2 pr-2">
                            <Badge variant={r.status === "success" ? "outline" : "destructive"} className="text-xs">
                              {r.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
